// apps/crm-frontend/src/components/CustomerLimitBanner.tsx
import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { UserContext } from '../context/UserContext';
import { getCustomerLimitInfo } from '../services/api';
import { isSubscriptionEnabled } from '../utils/environment';

interface CustomerLimitInfo {
  isWithinLimit: boolean;
  currentCount: number;
  limit: number;
  planName: string;
  exceeded: number;
}

/** 還能新增客戶：無上限（0）或目前數量未達上限（後端 assertCustomerQuota 同規則） */
export const canCreateCustomer = (info: { currentCount: number; limit: number }) => info.limit === 0 || info.currentCount < info.limit;

interface CustomerLimitBannerProps {
  /** 未超過上限（超過時鎖住專案、報價等新增；剛好等於上限仍可新增其他資料） */
  onLimitChange?: (canAddCustomer: boolean) => void;
  /** 還能新增客戶（等於上限就不能再新增客戶） */
  onCanCreateCustomerChange?: (canCreate: boolean) => void;
  showAlways?: boolean; // 管理員頁面始終顯示
  refreshTrigger?: number; // 當這個值變化時，重新獲取客戶限制資訊
}

// Docker 版本不顯示客戶限制橫幅，但仍要通知父組件可以新增客戶（沒有限制）
// 拆成獨立元件：Hooks 不能放在 if 裡（React Rules of Hooks）
const UnlimitedNotifier: React.FC<Pick<CustomerLimitBannerProps, 'onLimitChange' | 'onCanCreateCustomerChange'>> = ({ onLimitChange, onCanCreateCustomerChange }) => {
  useEffect(() => {
    onLimitChange?.(true);
    onCanCreateCustomerChange?.(true);
  }, [onLimitChange, onCanCreateCustomerChange]);
  return null;
};

const CustomerLimitBanner: React.FC<CustomerLimitBannerProps> = (props) =>
  isSubscriptionEnabled() ? <SubscriptionLimitBanner {...props} /> : <UnlimitedNotifier onLimitChange={props.onLimitChange} onCanCreateCustomerChange={props.onCanCreateCustomerChange} />;

const SubscriptionLimitBanner: React.FC<CustomerLimitBannerProps> = ({ onLimitChange, onCanCreateCustomerChange, showAlways = false, refreshTrigger }) => {
  const { t } = useTranslation();
  const userContext = useContext(UserContext);
  const { user } = userContext!;

  // 嘗試從 localStorage 恢復之前的狀態，避免初次載入時的跳動
  const [limitInfo, setLimitInfo] = useState<CustomerLimitInfo | null>(() => {
    if (!user?.organizationId) return null;
    try {
      const cached = localStorage.getItem(`customer-limit-${user.organizationId}`);
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [isInitialLoading, setIsInitialLoading] = useState(!limitInfo); // 如果有快取，不需要初次載入狀態

  const fetchLimitInfo = useCallback(async (isInitial = false) => {
    if (!user?.id) return;
    
    try {
      if (isInitial) {
        setIsInitialLoading(true);
      }
      
      const response = await getCustomerLimitInfo();
      const data = response.data;
      setLimitInfo(data);

      // 將數據快取到 localStorage，下次載入時可以立即顯示
      try {
        localStorage.setItem(`customer-limit-${user.organizationId}`, JSON.stringify(data));
      } catch (error) {
        console.warn('Failed to cache customer limit info:', error);
      }
      
      // 通知父組件是否可以新增
      onLimitChange?.(data.isWithinLimit);
      onCanCreateCustomerChange?.(canCreateCustomer(data));
    } catch (error) {
      console.error('Failed to fetch customer limit info:', error);
    } finally {
      if (isInitial) {
        setIsInitialLoading(false);
      }
    }
  }, [user?.id, onLimitChange, onCanCreateCustomerChange]);

  useEffect(() => {
    if (user?.id) {
      fetchLimitInfo(true);
    }
  }, [user?.id, fetchLimitInfo]);

  // 當 refreshTrigger 變化時，重新獲取客戶限制資訊
  useEffect(() => {
    if (user?.id && refreshTrigger !== undefined && refreshTrigger > 0) {
      fetchLimitInfo(false);
    }
  }, [refreshTrigger, user?.id, fetchLimitInfo]);

  // 如果是初次載入且沒有快取，不顯示組件避免閃爍
  if (isInitialLoading && !limitInfo) {
    return null;
  }

  // 移除載入狀態的顯示，避免組件消失造成畫面跳動
  // 刷新時保持顯示舊數據，在背景更新

  if (!limitInfo) return null;

  // 一般頁面只在超過限制時顯示，管理員頁面始終顯示
  if (!showAlways && limitInfo.isWithinLimit) {
    return null;
  }

  const getProgressPercentage = () => {
    if (limitInfo.limit === 0) return 0; // 無限制
    return Math.min((limitInfo.currentCount / limitInfo.limit) * 100, 100);
  };


  return (
    // 用量資訊：平常是中性細框；只有超過上限（需要使用者處理）才用紅色（§3-5），不放 emoji
    <div className={`rounded-lg px-4 py-3 mb-6 border ${
      limitInfo.isWithinLimit
        ? 'bg-surface border-line text-fg'
        : 'border-red-300 dark:border-red-800 text-red-700 dark:text-red-400'
    }`}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center">
          <div>
            <h3 className="text-sm">
              {limitInfo.limit === 0 ? (
                <Trans
                  key="unlimited"
                  i18nKey="resourceLimit:unlimitedUsage"
                  values={{
                    planName: t(`resourceLimit:plans.${limitInfo.planName}`, limitInfo.planName),
                    currentCount: limitInfo.currentCount,
                    entityName: t('translation:entityNames.customer')
                  }}
                  components={{
                    bold: <strong key="bold-unlimited" />
                  }}
                />
              ) : (
                <span key="limited">
                  <Trans
                    key="limited-usage"
                    i18nKey="resourceLimit:limitedUsage"
                    values={{
                      planName: t(`resourceLimit:plans.${limitInfo.planName}`, limitInfo.planName),
                      currentCount: limitInfo.currentCount,
                      limit: limitInfo.limit,
                      entityName: t('translation:entityNames.customer')
                    }}
                    components={{
                      bold: <strong key="bold-limited" />
                    }}
                  />
                  {!limitInfo.isWithinLimit && (
                    <span key="exceeded" className="ml-2 font-medium">
                      {t('resourceLimit:exceeded', { exceeded: limitInfo.exceeded })}
                    </span>
                  )}
                </span>
              )}
            </h3>
            {!limitInfo.isWithinLimit && (
              <p className="text-[13px] mt-0.5">
                {t('resourceLimit:archiveOrUpgrade', { exceeded: limitInfo.exceeded, entityName: t('translation:entityNames.customer') })}
              </p>
            )}
          </div>
        </div>

        {limitInfo.limit > 0 && (
          <div className="flex items-center gap-3 shrink-0">
            <div className="w-32 bg-slate-100 dark:bg-slate-800 rounded-full h-1.5">
              <div 
                className={`h-1.5 rounded-full ${
                  limitInfo.isWithinLimit ? 'bg-accent' : 'bg-red-500'
                }`}
                style={{ width: `${getProgressPercentage()}%` }}
              />
            </div>
            <span className="text-[13px] tabular-nums text-fg-secondary">
              {Math.round(getProgressPercentage())}%
            </span>
          </div>
        )}
      </div>

    </div>
  );
};

export default CustomerLimitBanner;